import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, Star, Quote } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '@/contexts/DataContext';

const TestimonialSlider = ({ showAll = false, className = "" }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const { testimonials } = useData()

  const displayTestimonials = showAll ? testimonials : testimonials.slice(0, 4);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === displayTestimonials.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [displayTestimonials.length, isAutoPlaying]);

  const goToNext = () => {
    setCurrentIndex(currentIndex === displayTestimonials.length - 1 ? 0 : currentIndex + 1);
  };

  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? displayTestimonials.length - 1 : currentIndex - 1);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const getTagColor = (tag) => {
    const colors = {
      'USA Client': 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
      'USA': 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
      'UK Client': 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300',
      'UK': 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300',
      'Cyprus Client': 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300',
      'Cyprus': 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300',
      'Spain Client': 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
      'Spain': 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
      'Canada Client': 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
      'Canada': 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
      'Australia': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300',
      'Australia Client': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300',
      'Pakistan': 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
    };
    return colors[tag] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
  };

  if (!displayTestimonials || displayTestimonials.length === 0) {
    return <p className="text-center">No testimonials available</p>;
  }
  return (
    <div className={`relative ${className}`}
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}>

      {/* Main Testimonial Display */}
      <div className="relative overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -300 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
              duration: 0.5
            }}
            className="w-full"
          >
            <Card className="border-none shadow-2xl bg-gradient-to-br from-white to-purple-50 dark:from-card dark:to-purple-950/20">
              <CardContent className="p-8 md:p-12">
                <div className="grid md:grid-cols-3 gap-8 items-center">
                  {/* Client Info */}
                  <div className="text-center md:text-left">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                      className="relative inline-block mb-4"
                    >
                      <img
                        src={displayTestimonials[currentIndex]?.images?.url}
                        alt={displayTestimonials[currentIndex]?.name}
                        className="w-24 h-24 mx-auto rounded-full object-cover ring-4 ring-purple-100 dark:ring-purple-900/20"
                      />
                      <div className="absolute -top-2 -right-2 bg-purple-600 rounded-full p-1">
                        <Quote className="w-4 h-4 text-white" />
                      </div>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="space-y-2"
                    >
                      <h4 className="text-xl font-bold">
                        {displayTestimonials[currentIndex]?.name}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {displayTestimonials[currentIndex]?.position}
                      </p>
                      <p className="text-sm font-medium text-purple-600">
                        {displayTestimonials[currentIndex]?.company}
                      </p>
                      <Badge className={getTagColor(displayTestimonials[currentIndex]?.projectTitle)}>
                        {displayTestimonials[currentIndex]?.projectTitle}
                      </Badge>
                    </motion.div>
                  </div>

                  {/* Testimonial Content */}
                  <div className="md:col-span-2 space-y-6">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.4 }}
                      className="flex items-center gap-1"
                    >
                      {[...Array(displayTestimonials[currentIndex].rating)].map((_, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.5 + i * 0.1 }}
                        >
                          <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                        </motion.div>
                      ))}
                      <span className="text-sm text-muted-foreground ml-2">
                        ({displayTestimonials[currentIndex].rating}.0)
                      </span>
                    </motion.div>

                    <motion.blockquote
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 }}
                      className="text-lg md:text-xl leading-relaxed text-gray-700 dark:text-gray-300 italic"
                    >
                      "{displayTestimonials[currentIndex].message}"
                    </motion.blockquote>

                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.7 }}
                      className="flex items-center gap-2"
                    >
                      <Badge variant="outline" className="text-xs">
                        {displayTestimonials[currentIndex].projectTitle}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        • {displayTestimonials[currentIndex].country}
                      </span>
                    </motion.div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation Controls */}
      <div className="flex items-center justify-center mt-8 gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={goToPrevious}
          className="rounded-full hover:bg-purple-50 dark:hover:bg-purple-950/20"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>

        <div className="flex space-x-2">
          {displayTestimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentIndex
                ? 'bg-purple-600 scale-110'
                : 'bg-gray-300 dark:bg-gray-600 hover:bg-purple-400'
                }`}
            />
          ))}
        </div>

        <Button
          variant="outline"
          size="icon"
          onClick={goToNext}
          className="rounded-full hover:bg-purple-50 dark:hover:bg-purple-950/20"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Client Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="mt-8 text-center"
      >
        <div className="grid grid-cols-3 gap-6 max-w-md mx-auto">
          <div>
            <div className="text-2xl font-bold text-purple-600">60+</div>
            <div className="text-xs text-muted-foreground">Happy Clients</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-purple-600">8+</div>
            <div className="text-xs text-muted-foreground">Countries</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-purple-600">100%</div>
            <div className="text-xs text-muted-foreground">Satisfaction</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default TestimonialSlider;